
import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatOption } from '@angular/material/autocomplete';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSelect } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { RecurringScheduledMaintenanceTaskSeriesCreateRequest, ScheduledMaintenanceRecurrenceKind } from '../scheduled-maintenance-recurrence.model';

export type ScheduleMode = 'OneTime' | 'Recurring';

@Component({
    selector: 'maintenance-recurrence',
    templateUrl: './maintenance-recurrence.component.html',
    styleUrls: ['./maintenance-recurrence.component.less'],
    imports: [ReactiveFormsModule, MatCheckbox, MatFormField, MatInput, MatLabel, MatOption, MatRadioButton, MatRadioGroup, MatSelect]
})
export class MaintenanceRecurrenceComponent implements OnDestroy {
    @Output() scheduleModeChanged: EventEmitter<ScheduleMode> = new EventEmitter<ScheduleMode>();

    weekdays: string[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    scheduleMode: FormControl<ScheduleMode> = new FormControl<ScheduleMode>('OneTime', { nonNullable: true });
    recurrenceForm: FormGroup = new FormGroup({
        firstLocalStartDateTime: new FormControl('', Validators.required),
        recurrenceKind: new FormControl<ScheduledMaintenanceRecurrenceKind>('Daily', { nonNullable: true, validators: [Validators.required] }),
        interval: new FormControl(1, [Validators.required, Validators.min(1)]),
        weekdays: new FormControl<string[]>([], { nonNullable: true }),
        dayOfMonth: new FormControl(1, [Validators.min(1), Validators.max(31)]),
        endLocalDateTime: new FormControl(''),
        maxOccurrenceCount: new FormControl(null, Validators.min(1))
    });

    private readonly scheduleModeSubscription: Subscription;

    constructor() {
        this.scheduleModeSubscription = this.scheduleMode.valueChanges.subscribe(value => this.scheduleModeChanged.emit(value));
    }

    ngOnDestroy(): void {
        this.scheduleModeSubscription.unsubscribe();
    }

    isRecurring(): boolean {
        return this.scheduleMode.value === 'Recurring';
    }

    isValid(): boolean {
        if (!this.isRecurring()) {
            return true;
        }

        if (this.recurrenceForm.invalid) {
            return false;
        }

        if (!this.firstStartIsOnTheHour()) {
            return false;
        }

        if (this.recurrenceForm.value.recurrenceKind === 'Weekly') {
            return this.recurrenceForm.value.weekdays.length > 0;
        }

        if (this.recurrenceForm.value.recurrenceKind === 'Monthly') {
            return this.recurrenceForm.value.dayOfMonth !== null && this.recurrenceForm.value.dayOfMonth !== '';
        }

        return true;
    }

    private firstStartIsOnTheHour(): boolean {
        const firstLocalStartDateTime = new Date(this.recurrenceForm.value.firstLocalStartDateTime);
        return firstLocalStartDateTime.getMinutes() === 0 && firstLocalStartDateTime.getSeconds() === 0;
    }

    buildRecurrenceRequest(timeZoneId: string): Omit<RecurringScheduledMaintenanceTaskSeriesCreateRequest, 'TaskConfiguration' | 'TenantIds'> {
        const value = this.recurrenceForm.value;
        const request: Omit<RecurringScheduledMaintenanceTaskSeriesCreateRequest, 'TaskConfiguration' | 'TenantIds'> = {
            TimeZoneId: timeZoneId,
            FirstLocalStartDateTime: new Date(value.firstLocalStartDateTime),
            RecurrenceKind: value.recurrenceKind,
            Interval: Number(value.interval),
            Weekdays: value.weekdays
        };

        if (value.recurrenceKind === 'Monthly' && value.dayOfMonth !== null && value.dayOfMonth !== '') {
            request.DayOfMonth = Number(value.dayOfMonth);
        }

        if (value.endLocalDateTime) {
            request.EndLocalDateTime = new Date(value.endLocalDateTime);
        }

        if (value.maxOccurrenceCount !== null && value.maxOccurrenceCount !== '') {
            request.MaxOccurrenceCount = Number(value.maxOccurrenceCount);
        }

        return request;
    }

    toggleWeekday(day: string, checked: boolean): void {
        const weekdays = this.recurrenceForm.value.weekdays as string[];
        const updatedWeekdays = checked
            ? [...weekdays, day]
            : weekdays.filter(weekday => weekday !== day);

        this.recurrenceForm.patchValue({ weekdays: updatedWeekdays });
    }
}
